import { getCsvFileName } from '../../../util/fileExport';

/**
 * 計測データのファイル名を生成する
 * @param {string} dataName データ名
 * @param {string} functionName 機能名
 * @param {string} vin VIN
 * @returns {string} 生成されたファイル名
 */
export function getMeasurementFileName(dataName, functionName, vin) {
  return getCsvFileName(functionName, dataName, vin);
}


/**
 * ファイル作成クラス。
 * 大量データのファイル作成ができるように、File System Access APIを使用してファイルに直接書き込む。
 */
class FileWriter {
  __fileSettings = {};
  __writableStream = null;
  __textEncoder = null;

  // ユーザーがファイル選択でキャンセルした場合に発生するエラー
  static get ABORT_ERROR() { return 'AbortError'; }

  /** 
   * コンストラクタ
   * @param {string} fileName ファイル名
   */
  constructor(fileName) {
    this.__fileSettings = {
      FILE_NAME: fileName,
      DESCRIPTION: 'TEXT',
      ACCEPT: { 'text/plain': ['.txt'] }
    };
  }

  /**
   * 初期化
   */
  async init() {
    // ユーザーに保存先を選択してもらう
    const fileHandle = await window.showSaveFilePicker({
      suggestedName: this.__fileSettings.FILE_NAME,
      types: [{
        description: this.__fileSettings.DESCRIPTION,
        accept: this.__fileSettings.ACCEPT
      }],
      excludeAcceptAllOption: true
    });
    this.__writableStream = await fileHandle.createWritable();
    this.__textEncoder = new TextEncoder();
  }

  /**
   * ファイルへの書き込みを終了する
   */
  async close() {
    this.__writableStream && await this.__writableStream.close();
    // 2重に終了しようとしてエラーになるのを防ぐため、終了後はストリームをnullにする
    this.__writableStream = null;
  }

  /**
   * ファイルへの書き込み
   * @param {string} dataStr 書き込む文字列
   */
  async write(dataStr) {
    if (!this.__writableStream || !this.__textEncoder) {
      throw new Error('FileWriter is not initialized. Please call init() before write().');
    }
    await this.__writableStream.write(this.__textEncoder.encode(dataStr));
  }

  /**
   * 発生したエラーがユーザーキャンセルによるエラーか否かを返す
   * @param {object} error エラーオブジェクト
   * @returns {boolean} ユーザーキャンセルによるエラーか否か
   */
  static isUserCancel(error) {
    return error.name === FileWriter.ABORT_ERROR;
  }
}

/**
 * CSVファイル作成クラス。FileWriter を継承して、CSV用の設定や処理を追加する。
 */
export class CsvFileWriter extends FileWriter {
  /** 
   * コンストラクタ。CSV用の設定値にするため、オーバーライド
   * @param {string} fileName ファイル名
   */
  constructor(fileName) {
    super(fileName);
    this.__fileSettings = {
      ...this.__fileSettings,
      DESCRIPTION: 'CSV',
      ACCEPT: { 'text/csv': ['.csv'] },
      UTF8_BOM: [0xEF, 0xBB, 0xBF]
    };
  }

  /**
   * 初期化。CSVの場合はUTF-8 BOMをファイルの先頭に書き込むため、オーバーライド
   * @param {boolean} ignoreUserCancel ユーザーがファイル選択をキャンセルした場合のエラーを無視するかどうか
   */
  async init(ignoreUserCancel = true) {
    await super.init(ignoreUserCancel);
    await this.__writableStream.write(new Uint8Array(this.__fileSettings.UTF8_BOM));
  }
}

export default {
  getMeasurementFileName,
  FileWriter,
  CsvFileWriter,
};